import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { Modal } from 'src/components/modals/comps';
import { DzoneWrap, CsvDropzoneWithTemplate, CsvUploadButtons, CsvValidationErrors, SCVSummary } from 'src/components/CsvUpload';
import { Poppins, Spacer } from 'src/common';
import { Variable } from 'src/api/types';
import { showSuccessToast } from 'src/components/Toast';
import { generateScenarioPackTemplateLink, parseScenarioPackCsv } from './import';
import { parseGraphqlCsvValidationErrors, ValidationErrorItem } from 'src/utils/csv';
import { ContentImportSummary, ScenarioPackImportPayload } from 'src/types/contentImport';
import {
  IMPORT_SCENARIO_GROUP,
  ImportScenarioGroupMutation,
  ImportScenarioGroupMutationVariables,
} from 'src/graphql/contentImport';

const StyledModal = styled(Modal)`
  width: 920px;
  padding: 0 !important;
`;

interface ScenarioGroupImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => Promise<void> | void;
  scenarioGroupId: string;
  groupName?: string | null;
  existingScenarioIds: string[];
  workspaceVariables: Variable[];
}

export const ScenarioGroupImportModal: React.FC<ScenarioGroupImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
  scenarioGroupId,
  groupName,
  existingScenarioIds,
  workspaceVariables,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [payload, setPayload] = useState<ScenarioPackImportPayload | null>(null);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [completedSummary, setCompletedSummary] = useState<ContentImportSummary | null>(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [runImportScenarioGroup] = useMutation<ImportScenarioGroupMutation, ImportScenarioGroupMutationVariables>(
    IMPORT_SCENARIO_GROUP,
  );

  const templateLink = useMemo(() => generateScenarioPackTemplateLink(), []);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(templateLink);
    };
  }, [templateLink]);

  const resetState = () => {
    setFile(null);
    setValidationErrors([]);
    setPayload(null);
    setCreatedCount(0);
    setUpdatedCount(0);
    setCompletedSummary(null);
    setError('');
    setIsUploading(false);
  };

  const handleClose = () => {
    if (isUploading) return;
    resetState();
    onClose();
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setCompletedSummary(null);
    setError('');

    const parsed = await parseScenarioPackCsv({
      file: selectedFile,
      variables: workspaceVariables,
      existingScenarioIds,
    });

    setPayload(parsed.payload);
    setValidationErrors(parsed.validationErrors);
    setCreatedCount(parsed.createdCount);
    setUpdatedCount(parsed.updatedCount);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const upload = async () => {
    if (!payload) return;

    setIsUploading(true);
    setError('');
    setValidationErrors([]);

    try {
      const { data } = await runImportScenarioGroup({
        variables: {
          scenarioGroupId,
          input: payload,
        },
      });
      const summary = data?.importScenarioGroup;
      if (!summary) {
        throw new Error('Import failed');
      }
      setCompletedSummary(summary);
      await onImported();
      showSuccessToast({ title: 'Scenario pack imported' });
    } catch (err) {
      const parsedValidationErrors = parseGraphqlCsvValidationErrors(err);
      if (parsedValidationErrors.length > 0) {
        setValidationErrors(parsedValidationErrors);
        setError('');
        return;
      }
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isUploading}>
      <Spacer $px={40} />
      <div className="h-padding">
        <Poppins className="m-title" px={28}>
          Import Scenario Pack
        </Poppins>
        <Spacer $px={10} />
        <Poppins px={14}>{groupName || 'Scenario pack'}</Poppins>
        <Spacer $px={30} />
      </div>

      <DzoneWrap className="h-padding">
        <CsvDropzoneWithTemplate
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          file={file}
          templateLink={templateLink}
          templateFileName="scenario_pack_template.csv"
        />

        <CsvValidationErrors errors={validationErrors} />

        {file && !validationErrors.length && !completedSummary && (
          <SCVSummary
            items={[
              'File validated',
              'Scenario pack metadata ready to be updated',
              `${updatedCount} scenarios ready to be updated`,
              `${createdCount} scenarios ready to be created`,
            ]}
          />
        )}

        {completedSummary && (
          <SCVSummary
            isCompleted
            items={[
              completedSummary.metadataUpdated ? 'Scenario pack metadata updated' : 'Scenario pack metadata unchanged',
              `${completedSummary.updatedCount} existing scenarios updated`,
              `${completedSummary.createdCount} new scenarios added`,
              `${completedSummary.unchangedCount} scenarios unchanged`,
              'File upload successful',
            ]}
          />
        )}

        {error && (
          <div className="error">
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </div>
        )}

        <CsvUploadButtons
          isUploadSuccessful={!!completedSummary}
          hasFile={!!file}
          isDisabled={!payload || !!validationErrors.length || isUploading}
          isUploading={isUploading}
          onCancel={handleClose}
          onUpload={upload}
          onSelectFile={open}
          onUploadAnother={resetState}
          onDone={handleClose}
        />
      </DzoneWrap>
      <Spacer $px={40} />
    </StyledModal>
  );
};
